import React, { useState } from "react";

import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/router";
import Link from "next/link";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { Checkbox } from "primereact/checkbox";

import TermsAndConditionsModal from "@/components/TermsAndConditionsModal";
import PrivacyAndPolicyModal from "@/components/PrivacyAndPolicyModal";

import { AuthServices } from "@/services/auth.services";

import { handlingErrorMessage } from "@/utils/handlingErrorMessage";
import { setRegisteredDate } from "@/event/mixpanel";

const TalentRegister = () => {
  const router = useRouter();
  const [stepSlider, setStepSlider] = useState(0);
  const [termsModal, setTermsModal] = useState(false);
  const [privacyModal, setPrivacyModal] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      termAndConditions: false,
      privacyAndPolicy: false,
    },
  });

  const getFormErrorMessage = (name: string) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const { mutate, isLoading, error } = useMutation(
    (formData: { email: string; password: string }) =>
      AuthServices.registerWithEmailAndPassword(
        formData.email,
        formData.password,
        "talent"
      ),
    {
      onSuccess: (user) => {
        router.push("verification-email");
      },
    }
  );

  const { mutate: mutateRegisterWithGoogle, error: errorRegisterWithGoogle } =
    useMutation(() => AuthServices.signInWithGoogle("talent"), {
      onSuccess: () => {},
    });

  // const {
  //   mutate: mutateRegisterWithFacebook,
  //   error: errorRegisterWithFacebook,
  // } = useMutation(() => AuthServices.signInWithFacebook('talent'), {
  //   onSuccess: () => {},
  // });

  const { mutate: mutateRegisterWithGithub, error: errorRegisterWithGithub } =
    useMutation(() => AuthServices.signInWithGithub("talent"), {
      onSuccess: () => {},
    });

  const onSubmit = async (formData: any) => {
    mutate(formData);
  };

  return (
    <>
      <div className="surface-card border-round w-ful">
        <div className="text-center">
          <div className="text-900 text-3xl font-medium mb-3 text-primary">
            Create an account
          </div>
        </div>

        <div className="flex justify-content-center gap-3 flex-column md:flex-row">
          <Button
            onClick={() => mutateRegisterWithGoogle()}
            className="w-full lg:w-6 font-medium border-1 surface-border surface-100 hover:surface-200 active:surface-300 text-900 inline-flex align-items-center justify-content-center py-3"
          >
            <i className="pi pi-google text-red-500 mr-2"></i>
            <span>Register With Google</span>
          </Button>
          {/* <Button
            onClick={() => mutateRegisterWithFacebook()}
            className="w-6 font-medium border-1 surface-border surface-100 hover:surface-200 active:surface-300 text-900 inline-flex align-items-center justify-content-center py-3"
          >
            <i className="pi pi-facebook text-blue-500 mr-2"></i>
            <span>Register With Facebook</span>
          </Button> */}
          <Button
            onClick={() => mutateRegisterWithGithub()}
            className="w-full lg:w-6 font-medium border-1 surface-border surface-100 hover:surface-200 active:surface-300 text-900 inline-flex align-items-center justify-content-center py-3"
          >
            <i className="pi pi-github text-black-500 mr-2"></i>
            <span>Register With Github</span>
          </Button>
        </div>
        <Divider align="center" className="">
          <span className="text-600 font-normal text-sm">OR</span>
        </Divider>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-3">
            <label
              htmlFor="email"
              className="block text-900 font-medium mb-2 text-primary"
            >
              Email <span className="text-red-500">*</span>
            </label>

            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required.",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  id="email"
                  type="text"
                  className={`w-full ${errors.email?.message && "p-invalid"}`}
                />
              )}
            />
            {getFormErrorMessage("email")}
          </div>
          <div className="mb-3">
            <label
              htmlFor="password"
              className="block text-900 font-medium mb-2 text-primary"
            >
              Password <span className="text-red-500">*</span>
            </label>
            <Controller
              name="password"
              control={control}
              rules={{
                required: "Password is required.",
                maxLength: 30,
                minLength: 6,
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  id="password"
                  type="password"
                  className={`w-full ${errors.email?.message && "p-invalid"}`}
                />
              )}
            />
            {getFormErrorMessage("password")}
          </div>
          <div className="mb-3">
            <label
              htmlFor="Confirmpassword"
              className="block text-900 font-medium mb-2 text-primary"
            >
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Confirm password is required.",
                validate: (value: string) => {
                  if (watch("password") != value) {
                    return "Your passwords don't match";
                  }
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  id="confirmPassword"
                  type="password"
                  className={`w-full ${errors.email?.message && "p-invalid"}`}
                />
              )}
            />
            {getFormErrorMessage("confirmPassword")}
          </div>

          <div className="flex flex-column align-items-start justify-content-between mb-2">
            <div className="flex align-items-center mb-2">
              <Controller
                name="termAndConditions"
                control={control}
                rules={{
                  required: "Please accept Terms and Conditions.",
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Checkbox
                      inputId={field.name}
                      checked={field.value}
                      inputRef={field.ref}
                      className={`mr-2 ${fieldState.error && "p-invalid"}`}
                      onChange={(e) => field.onChange(e.checked)}
                    />
                    <label
                      className={`text-primary block ${
                        errors.email?.message && "p-invalid"
                      }`}
                      // htmlFor="termAndConditions"
                    >
                      Accept{" "}
                      <span
                        onClick={() => setTermsModal(true)}
                        className="cursor-pointer text-blue-500 text-900"
                      >
                        Terms and Conditions
                      </span>
                      <span className="text-red-500">*</span>
                    </label>
                  </>
                )}
              />
            </div>
            {getFormErrorMessage("termAndConditions")}
          </div>
          <div className="flex flex-column align-items-start justify-content-between mb-6">
            <div className="flex align-items-center mb-2">
              <Controller
                name="privacyAndPolicy"
                control={control}
                rules={{
                  required: "Please accept Privacy Policy.",
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Checkbox
                      inputId={field.name}
                      checked={field.value}
                      inputRef={field.ref}
                      className={`mr-2 ${fieldState.error && "p-invalid"}`}
                      onChange={(e) => field.onChange(e.checked)}
                    />
                    <label
                      className={`text-primary block ${
                        errors.privacyAndPolicy?.message && "p-invalid"
                      }`}
                      // htmlFor="termAndConditions"
                    >
                      Accept{" "}
                      <span
                        onClick={() => setPrivacyModal(true)}
                        className="cursor-pointer text-blue-500 text-900"
                      >
                        Privacy Policy
                      </span>
                      <span className="text-red-500">*</span>
                    </label>
                  </>
                )}
              />
            </div>
            {getFormErrorMessage("privacyAndPolicy")}
          </div>

          <Button label="Sign Up" className="w-full" loading={isLoading} />
        </form>
        <p className="p-invalid mt-2 text-center text-red-500">
          {!!errorRegisterWithGoogle?.code &&
            handlingErrorMessage(errorRegisterWithGoogle?.code)}
          {/* {!!errorRegisterWithFacebook?.code &&
            handlingErrorMessage(!!errorRegisterWithFacebook?.code)} */}
          {!!error?.code && handlingErrorMessage(error?.code)}
          {!!errorRegisterWithGithub?.code &&
            handlingErrorMessage(errorRegisterWithGithub?.code)}
        </p>
        <div className="text-center mt-5">
          <span className="text-600 font-medium line-height-3 text-primary-500">
            Already have an account?
          </span>
          <Link
            href="/auth/login"
            className="font-medium no-underline ml-2 text-blue-500 cursor-pointer text-orange-300"
          >
            Sign in!
          </Link>
        </div>
        <div className="mt-5 text-gray-400 text-sm">
          By clicking "Register with Google/Github/Facebook" above, you
          acknowledge that you have read and understood, and agree to
          portfolii's
          <span
            className="underline cursor-pointer"
            onClick={() => setTermsModal(true)}
          >
            {" "}
            Terms & Conditions
          </span>{" "}
          and{" "}
          <span
            className="underline cursor-pointer"
            onClick={() => setPrivacyModal(true)}
          >
            Privacy Policy
          </span>
          .
        </div>
      </div>
      <TermsAndConditionsModal
        onHide={() => setTermsModal(false)}
        visible={termsModal}
      />
      <PrivacyAndPolicyModal
        onHide={() => setPrivacyModal(false)}
        visible={privacyModal}
      />
    </>
  );
};

export default TalentRegister;
